import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { PiEyeBold, PiEyeSlashBold, PiWarningCircleBold, PiSpinnerBold } from 'react-icons/pi';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

/*
 * Sign-in screen: a floating card over a blurred field of the farm's own
 * photography.
 *
 * Shape lock, by nesting depth. Outer surfaces are softest, controls are
 * tightest, and nothing on this page uses a radius outside this scale:
 *   card 28px  >  media 18px  >  controls 12px  >  brand mark 10px
 */
const R_CARD = '28px';
const R_MEDIA = '18px';
const R_CONTROL = '12px';
const R_MARK = '10px';

const SLIDE_MS = 6000;

/* ---------- motion ---------------------------------------------------------
 * Each animation states what it communicates:
 *   liftIn  - hierarchy: establishes the card as the subject on first paint
 *   riseIn  - hierarchy: reveals card contents in reading order
 *   drift   - atmosphere: keeps the photography from reading as a static plate
 *   nudge   - feedback: acknowledges a rejected credential
 *   spin    - feedback: the request is in flight
 * All of it collapses under prefers-reduced-motion.
 * ------------------------------------------------------------------------- */

const liftIn = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const drift = keyframes`
  from { transform: scale(1.04); }
  to   { transform: scale(1.14); }
`;

const nudge = keyframes`
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-5px); }
  40%      { transform: translateX(4px); }
  60%      { transform: translateX(-3px); }
  80%      { transform: translateX(2px); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Staggered entrance for card contents, applied only where motion is welcome.
const enter = (delay) => css`
  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    animation: ${riseIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s forwards;
  }
`;

/* ---------- stage and backdrop -------------------------------------------- */

const Stage = styled.div`
  position: relative;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: 24px;
  overflow: hidden;
  background: ${props => props.theme.colors.background};
`;

const Backdrop = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* 48px-wide source: blurring a thumbnail is visually identical to blurring the
     full frame and costs 2KB instead of 1.2MB. The scale hides the blur bleed.
     Desaturated because the grow lights push these frames hard into magenta, and
     at full chroma the backdrop shouts over the card. */
  filter: blur(36px) saturate(0.6);
  transform: scale(1.18);
  opacity: ${props => (props.$active ? 1 : 0)};
  transition: opacity 1.6s ease-in-out;
  pointer-events: none;

  @media (prefers-reduced-motion: no-preference) {
    ${props => props.$active && css`
      animation: ${drift} ${SLIDE_MS * 4}ms ease-out forwards;
    `}
  }
`;

const BackdropWash = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  /* Lifts the card off a busy photograph. Keyed to the active theme so the card
     keeps the same separation in both modes. */
  background: ${props => (props.$dark
    ? 'radial-gradient(120% 90% at 50% 45%, rgba(8, 14, 11, 0.68) 0%, rgba(8, 14, 11, 0.90) 100%)'
    : 'radial-gradient(120% 90% at 50% 45%, rgba(250, 252, 250, 0.56) 0%, rgba(250, 252, 250, 0.82) 100%)')};
`;

/* ---------- card ---------------------------------------------------------- */

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 980px;
  display: grid;
  /* Asymmetric split: photography takes the narrower column, the form leads. */
  grid-template-columns: 0.72fr 1fr;
  gap: 0;
  padding: 12px;
  border-radius: ${R_CARD};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  /* Tinted, never pure black. */
  box-shadow: 0 32px 80px -24px rgba(13, 31, 24, 0.42);

  @media (prefers-reduced-motion: no-preference) {
    animation: ${liftIn} 0.78s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-width: 480px;
    padding: 10px;
  }
`;

const Media = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${R_MEDIA};
  /* Stretches to whatever height the form column needs, so the card never ends
     in a band of dead space below the photograph. */
  height: 100%;
  min-height: 420px;
  background: #0d1f18;

  @media (max-width: 860px) {
    height: 190px;
    min-height: 0;
  }
`;

const Shot = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* cover, not contain: these are vertical phone frames, and letterboxing them
     is what made the old panel read as a placeholder. */
  object-fit: cover;
  opacity: ${props => (props.$active ? 1 : 0)};
  transition: opacity 1.6s ease-in-out;

  @media (prefers-reduced-motion: no-preference) {
    ${props => props.$active && css`
      animation: ${drift} ${SLIDE_MS * 4}ms ease-out forwards;
    `}
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(24px, 3.4vw, 44px) clamp(22px, 3.4vw, 44px);

  @media (max-width: 860px) {
    padding: 28px 20px 22px;
  }
`;

const Eyebrow = styled.p`
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.textMuted};
  margin: 0 0 6px 0;
  ${enter(0.18)}
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.display};
  font-optical-sizing: auto;
  font-weight: 400;
  font-size: clamp(2.125rem, 4.4vw, 3.125rem);
  /* 1.12 keeps descenders clear of the second line at display size. */
  line-height: 1.12;
  letter-spacing: -0.012em;
  color: ${props => props.theme.colors.text};
  margin: 0 0 26px 0;
  ${enter(0.24)}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => enter(props.$delay)}
`;

/* The reference this is modelled on uses placeholder-only fields. Placeholders
   vanish the moment you type and are not reliably announced, so the label stays,
   sized down to keep the composition quiet. */
const Label = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${props => props.theme.colors.textSecondary};
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 13px 15px;
  padding-right: ${props => (props.$hasIcon ? '46px' : '15px')};
  font-size: 0.9375rem;
  border-radius: ${R_CONTROL};
  border: 1px solid ${props => (props.$error ? props.theme.colors.danger : props.theme.colors.border)};
  /* background token inside a surface card, so the field reads as inset in both
     modes rather than white-on-white. */
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover:not(:focus) {
    border-color: ${props => props.theme.colors.borderHover};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    /* A real ring, not a 1px hue shift, so keyboard focus is unmissable. */
    box-shadow: 0 0 0 3px ${props => props.theme.colors.accent}2E;
  }

  &::placeholder {
    /* Full opacity: textMuted already clears AA against the field fill, and
       dimming it did not. */
    color: ${props => props.theme.colors.textMuted};
  }

  /* Keep Chrome autofill from painting its own background over the theme. */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: ${props => props.theme.colors.text};
    -webkit-box-shadow: 0 0 0 100px ${props => props.theme.colors.background} inset;
    caret-color: ${props => props.theme.colors.text};
  }
`;

const RevealToggle = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  background: none;
  border: none;
  border-radius: ${R_MARK};
  cursor: pointer;
  font-size: 1.0625rem;
  color: ${props => props.theme.colors.textMuted};
  transition: color 0.15s ease, box-shadow 0.15s ease;

  &:hover { color: ${props => props.theme.colors.text}; }

  &:focus-visible {
    outline: none;
    color: ${props => props.theme.colors.text};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.accent};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 13px;
  border-radius: ${R_CONTROL};
  background: ${props => props.theme.colors.dangerLight};
  color: ${props => props.theme.colors.danger};
  font-size: 0.875rem;
  line-height: 1.4;

  svg { flex-shrink: 0; font-size: 1.0625rem; }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${nudge} 0.36s ease-in-out;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
  padding: 14px;
  border: none;
  border-radius: ${R_CONTROL};
  background: ${props => props.theme.colors.primary};
  /* textInverse clears AA against primary in both themes (9.5:1 light, 4.8:1 dark). */
  color: ${props => props.theme.colors.textInverse};
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.18s ease, transform 0.12s ease, box-shadow 0.18s ease;
  ${enter(0.42)}

  &:hover:not(:disabled) {
    filter: brightness(1.12);
    box-shadow: 0 8px 22px -6px ${props => props.theme.colors.primary}80;
  }

  /* Physical push on press. */
  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.accent}66;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Spinner = styled(PiSpinnerBold)`
  font-size: 1.0625rem;
  animation: ${spin} 0.7s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-top: clamp(24px, 3.5vh, 36px);
  padding-top: clamp(18px, 2.6vh, 26px);
  border-top: 1px solid ${props => props.theme.colors.border};
  ${enter(0.5)}
`;

const Mark = styled.img`
  width: 30px;
  height: 30px;
  border-radius: ${R_MARK};
  object-fit: cover;
  flex-shrink: 0;
`;

const Wordmark = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${props => props.theme.colors.text};
`;

const Tagline = styled.span`
  font-size: 0.75rem;
  line-height: 1.35;
  color: ${props => props.theme.colors.textMuted};
  border-left: 1px solid ${props => props.theme.colors.border};
  padding-left: 11px;
  margin-left: 2px;

  /* Wraps to three ragged lines in a narrow card, which reads worse than not
     being there at all. */
  @media (max-width: 860px) {
    display: none;
  }
`;

/* The photography is ambient brand texture, not content the sign-in task depends
   on, so it is hidden from assistive tech rather than narrated on a 6s loop. */
const slides = ['strawberry', 'IMG-20250910-WA0007', 'IMG-20250910-WA0009', 'IMG-20250910-WA0010'];

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [slide, setSlide] = useState(0);
  // Mount only the frame in view plus the next one, so the card image budget is
  // one ~200KB request at a time instead of four up front.
  const [mounted, setMounted] = useState(() => new Set([0, 1]));
  const { login, sessionExpired, clearSessionExpired } = useAuth();
  const { isDarkMode } = useTheme();

  // Sessions are permanent now (30 days) — this should be rare — but when it does
  // happen it is worth saying so plainly. Without it, a 401-triggered redirect
  // reads as the app randomly discarding whatever the user was doing (this is
  // what made a plant log's in-progress photos look like a hard "2 images" cap:
  // the session, not the photo count, was what ran out).
  useEffect(() => {
    if (sessionExpired) {
      setError('Your session expired. Sign in again — anything you were entering was not saved.');
      clearSessionExpired();
    }
  }, [sessionExpired, clearSessionExpired]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSlide(prev => (prev + 1) % slides.length);
    }, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [slide]);

  useEffect(() => {
    setMounted(prev => {
      const next = (slide + 1) % slides.length;
      if (prev.has(slide) && prev.has(next)) return prev;
      const updated = new Set(prev);
      updated.add(slide);
      updated.add(next);
      return updated;
    });
  }, [slide]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    // On success the auth state flips and this component unmounts, so only the
    // failure path needs handling here.
    if (!result.success) {
      setError(result.error);
      setLoading(false);
    }
  }, [login, username, password]);

  return (
    <Stage>
      {slides.map((name, i) => (
        mounted.has(i) && (
          <Backdrop
            key={name}
            src={`/slides/blur/${name}.jpg`}
            alt=""
            aria-hidden="true"
            $active={i === slide}
            decoding="async"
          />
        )
      ))}
      <BackdropWash $dark={isDarkMode} aria-hidden="true" />

      <Card>
        <Media aria-hidden="true">
          {slides.map((name, i) => (
            mounted.has(i) && (
              <Shot
                key={name}
                src={`/slides/card/${name}.jpg`}
                alt=""
                $active={i === slide}
                fetchpriority={i === 0 ? 'high' : 'low'}
                decoding="async"
              />
            )
          ))}
        </Media>

        <Panel>
          <Eyebrow>Sign in to</Eyebrow>
          <Title>Your farm,<br />in real time</Title>

          <Form onSubmit={handleSubmit} noValidate>
            {error && (
              <ErrorMessage id="login-error" role="alert">
                <PiWarningCircleBold aria-hidden="true" />
                <span>{error}</span>
              </ErrorMessage>
            )}

            <Field $delay={0.3}>
              <Label htmlFor="login-username">Username</Label>
              <Input
                id="login-username"
                type="text"
                placeholder="Your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                $error={!!error}
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
                autoComplete="username"
                autoFocus
              />
            </Field>

            <Field $delay={0.33}>
              <Label htmlFor="login-password">Password</Label>
              <InputWrap>
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  $error={!!error}
                  $hasIcon
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  autoComplete="current-password"
                />
                <RevealToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <PiEyeSlashBold aria-hidden="true" /> : <PiEyeBold aria-hidden="true" />}
                </RevealToggle>
              </InputWrap>
            </Field>

            <SubmitButton type="submit" disabled={loading || !username || !password}>
              {loading && <Spinner aria-hidden="true" />}
              {loading ? 'Signing in' : 'Sign in'}
            </SubmitButton>
          </Form>

          <Lockup>
            <Mark src="/logo.jpg" alt="" />
            <Wordmark>NeuralKissan</Wordmark>
            <Tagline>Smart hydroponics monitoring.<br />Built in Goa.</Tagline>
          </Lockup>
        </Panel>
      </Card>
    </Stage>
  );
};

export default Login;
